import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, MessageCircle, ScanLine, GraduationCap, Languages, MapPin, Check } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/accordion';
import { Reveal, DemoButton, Eyebrow, H2 } from './ui';
import { FlowDiagram } from './Diagrams';
import { DRIVE_IQ, JOURNEY, PLANS, FAQ, SCENES } from './content';

const IQ_ICONS = [MessageCircle, ScanLine, GraduationCap, Languages];

export const DriveIqPanel = () => (
  <section id="drive-iq" className="dl-grain bg-[#0b1220] py-20 sm:py-28" data-testid="drive-iq-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#5eead4]/40 bg-[#5eead4]/10 px-3 py-1 text-xs font-bold text-[#5eead4]" data-testid="drive-iq-badge"><Sparkles size={13} /> Drive IQ</span>
      <H2 dark className="max-w-2xl">Know Your Business Inside Out.</H2>
      <p className="mt-4 max-w-xl text-[#94a3b8]">The intelligence inside Driveline. It works on your own workshop’s data, in plain language.</p>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {DRIVE_IQ.map((d, i) => {
          const Icon = IQ_ICONS[i];
          return (
            <Reveal key={d.title} delay={i * 90} className="rounded-3xl border border-[#1e293b] bg-[#111a2e] p-5" data-testid={`drive-iq-card-${i}`}>
              <Icon className="text-[#5eead4]" size={22} />
              <h3 className="dl-display mt-4 text-lg font-semibold text-[#f8fafc]">{d.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#94a3b8]">{d.line}</p>
              <div className="mt-5 rounded-xl border border-[#1e293b] bg-[#0b1220] px-3 py-2.5 font-mono text-xs text-[#5eead4]">
                <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#5eead4]" />{d.sample}
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const Journey = () => (
  <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-testid="journey">
    <Eyebrow>What your customer sees</Eyebrow>
    <H2 className="max-w-2xl">A service experience they tell their friends about.</H2>
    <div className="mt-12"><FlowDiagram steps={JOURNEY} testid="journey-diagram" /></div>
  </section>
);

// Placeholder until the founder and advisors send their names, photos and lines.
const FOUNDER = { name: 'Founder, Unisol Global', role: 'Vast technical experience in automotive care', story: 'We ran our detailing and body shop on paper, Excel and WhatsApp, and kept losing jobs, stock and sleep. So we built the system we wished we had, used it on our own floor every day, and now share it with other workshops.' };
const ADVISORS = [
  { name: 'Senior advisor', line: 'Decades in automotive service operations' },
  { name: 'Senior advisor', line: 'GST, accounts and compliance for workshops' },
  { name: 'Senior advisor', line: 'Dealer and parts business experience' },
];
const Monogram = ({ text, big }) => (
  <span className={`flex shrink-0 items-center justify-center rounded-2xl bg-[#0f172a] font-semibold text-[#5eead4] ${big ? 'h-20 w-20 text-2xl' : 'h-12 w-12 text-sm'}`}>{text}</span>
);

export const Team = () => (
  <section className="bg-[#efece4] py-20 sm:py-28" data-testid="team">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2">
      <Reveal>
        <Eyebrow>Built by people who run workshops</Eyebrow>
        <H2>Every screen was tested on a real car first.</H2>
        <p className="mt-5 text-[#475569]"><MapPin size={15} className="mr-1 inline text-[#0f766e]" />Unisol Global’s detailing and body-shop floor in Yelahanka, Bangalore is where Driveline was built, and where it still runs every day.</p>
        <img src={SCENES.advisor} alt="A service advisor showing a customer their report" loading="lazy" className="mt-8 aspect-[16/10] w-full rounded-3xl object-cover" />
      </Reveal>
      <div className="space-y-4">
        <Reveal className="rounded-3xl bg-[#ffffff] p-6" data-testid="founder-card">
          <div className="flex items-center gap-4"><Monogram text="UG" big /><div><h3 className="dl-display text-xl font-semibold text-[#0f172a]">{FOUNDER.name}</h3><p className="text-sm text-[#0f766e]">{FOUNDER.role}</p></div></div>
          <p className="mt-5 text-[15px] leading-relaxed text-[#334155]">“{FOUNDER.story}”</p>
        </Reveal>
        {ADVISORS.map((a, i) => (
          <Reveal key={a.line} delay={i * 80} className="flex items-center gap-4 rounded-2xl bg-[#ffffff] p-4" data-testid={`advisor-card-${i}`}>
            <Monogram text="SA" /><div><p className="font-semibold text-[#0f172a]">{a.name}</p><p className="text-sm text-[#475569]">{a.line}</p></div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const FoundingOffer = ({ proof }) => (
  <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8" data-testid="founding-offer">
    <Reveal className="grid gap-8 rounded-[2rem] border-2 border-[#0f172a] bg-[#ffffff] p-7 sm:p-10 lg:grid-cols-[1.4fr_1fr]">
      <div>
        <Eyebrow>Founding workshops</Eyebrow>
        <H2>The first 25 workshops get 30% off for 12 months.</H2>
        <ul className="mt-6 space-y-2.5 text-[15px] text-[#334155]">
          {['30% off any plan for your first 12 months', 'Priority onboarding: we set it up with you', 'A direct line to the founder'].map((t) => <li key={t} className="flex gap-2"><Check size={18} className="text-[#0f766e]" />{t}</li>)}
        </ul>
        <p className="mt-4 text-sm text-[#64748b]">In return: honest feedback, and permission to show your logo.</p>
      </div>
      <div className="flex flex-col justify-center rounded-3xl bg-[#0f172a] p-7 text-center">
        <p className="dl-display text-6xl font-semibold text-[#5eead4]" data-testid="founding-places-left">{proof ? proof.founding_places_left : '—'}</p>
        <p className="mt-1 text-sm text-[#cbd5e1]">of {proof?.founding_places_total || 25} places left</p>
        <DemoButton dark className="mt-6" testid="founding-book-demo">Claim a place</DemoButton>
      </div>
    </Reveal>
  </section>
);

export const PricingTeaser = () => (
  <section className="bg-[#efece4] py-20 sm:py-28" data-testid="pricing-teaser">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Eyebrow>Pricing</Eyebrow>
      <H2>Simple monthly plans. No setup fee.</H2>
      <div className="mt-10 grid gap-4 md:grid-cols-3">
        {PLANS.map((p) => (
          <div key={p.name} className={`rounded-3xl p-6 ${p.featured ? 'bg-[#0f172a] text-[#f8fafc]' : 'bg-[#ffffff] text-[#0f172a]'}`} data-testid={`plan-${p.name.toLowerCase()}`}>
            <p className={`font-semibold ${p.featured ? '!text-[#5eead4]' : ''}`}>{p.name}</p>
            <p className={`dl-display mt-3 text-4xl font-semibold ${p.featured ? '!text-[#f8fafc]' : ''}`}>₹{p.price.toLocaleString('en-IN')}<span className={`text-base font-medium ${p.featured ? '!text-[#94a3b8]' : '!text-[#64748b]'}`}> /month</span></p>
            <p className={`mt-2 text-sm ${p.featured ? '!text-[#cbd5e1]' : 'text-[#475569]'}`}>{p.blurb}</p>
          </div>
        ))}
      </div>
      <Link to="/driveline/pricing" className="mt-8 inline-block text-sm font-semibold text-[#0f766e] hover:text-[#0f172a]" data-testid="see-full-pricing">See full pricing →</Link>
    </div>
  </section>
);

export const Faq = ({ items = FAQ, title = 'Straight answers.' }) => (
  <section className="mx-auto max-w-3xl px-5 py-20 sm:px-8 sm:py-28" data-testid="faq">
    <Eyebrow>Questions</Eyebrow>
    <H2>{title}</H2>
    <Accordion type="single" collapsible className="mt-8">
      {items.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="border-[#e2e0d8]">
          <AccordionTrigger className="text-left text-base font-semibold text-[#0f172a]" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-[15px] leading-relaxed text-[#475569]">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const Closing = ({ title = 'See Driveline on your own workshop’s numbers.' }) => (
  <section className="px-5 pb-20 sm:px-8" data-testid="closing-cta">
    <div className="dl-grain mx-auto max-w-7xl rounded-[2rem] bg-[#0b1220] px-7 py-16 sm:px-14">
      <H2 dark className="max-w-2xl">{title}</H2>
      <p className="mt-4 max-w-lg text-[#94a3b8]">Thirty minutes, on a call, with your kind of workshop in mind.</p>
      <DemoButton dark className="mt-8" testid="closing-book-demo" />
    </div>
  </section>
);
